<template>
  <RevContextualMenu
    v-if="displayMultiLocaleSelector"
    content-class-names="z-10"
    data-test="multi-locale-selector"
    position="bottom-end"
    :should-hide-on-click-outside="true"
  >
    <template #trigger="{ isOpen, show, hide }">
      <RevListItemStatic
        class="text-action-default-hi focus-visible-outline-inset-hi cursor-pointer items-center whitespace-nowrap px-24 pt-20 capitalize hover:bg-static-default-min-hover md:pt-8"
        @click="onLocalesClick(isOpen, show, hide)"
      >
        <template #prefix>
          <RevCountryFlag
            class="rounded-xs min-w-18"
            :country-code
            size="small"
          />
        </template>

        <template #label>
          <p>{{ countryName }}</p>
        </template>

        <template #suffix>
          <div class="flex items-center">
            <p>{{ selectedLocale.label }}</p>

            <IconChevronUp v-if="isOpen" class="ml-4" />
            <IconChevronDown v-else class="ml-4" />
          </div>
        </template>
      </RevListItemStatic>
    </template>

    <template #items>
      <RevListItemAction
        v-for="option in localeOptions"
        :key="option.value.id"
        class="!py-12 !pl-[7px] !pr-16"
        :data-test="`multi-locale-selector-option-${option.value.id}`"
        :hreflang="option.value.id.split('-')?.[0]"
        :to="getLocaleLink(option)"
        @click="trackCountryChange(countryCode)"
      >
        <template #label>
          <div class="flex items-center">
            <IconCheckSmall
              v-if="
                selectedLocale.value.id === option.value.id &&
                currentLocaleInLocaleOptions
              "
            />

            <p
              :class="{
                'pl-24':
                  selectedLocale.value.id !== option.value.id ||
                  !currentLocaleInLocaleOptions,
              }"
            >
              {{ option.label }}
            </p>

            <RevTag
              v-if="option.value.id === BCP_47_LOCALES.es_US"
              class="ml-8"
              label="Beta"
              size="small"
              variant="info"
            />
          </div>
        </template>
      </RevListItemAction>
    </template>
  </RevContextualMenu>

  <RevListItemAction
    v-else
    class="text-action-default-hi items-center capitalize"
    :to="link"
    @click="trackCountryChange(countryCode)"
  >
    <template #prefix>
      <RevCountryFlag class="rounded-xs" :country-code size="small" />
    </template>
    <template #label>
      {{ countryName }}
    </template>
    <template #suffix>
      {{ displayName }}
    </template>
  </RevListItemAction>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import {
  BCP_47_LOCALES,
  type Bcp47Locale,
  type Locale,
} from '@backmarket/http-api'
import {
  type CountryCodeValue,
  MarketCountryCode,
} from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevContextualMenu } from '@ds/components/ContextualMenu'
import { RevCountryFlag } from '@ds/components/CountryFlag'
import { RevListItemAction } from '@ds/components/ListItemAction'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { RevTag } from '@ds/components/Tag'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconChevronUp } from '@ds/icons/IconChevronUp'

import { useLinkResolver } from '~/composables/useLinkResolver'
import { HOME } from '~/scopes/home/route-names'

import {
  countryDisplayNameBcp47,
  countryDisplayNameLocale,
} from '../../utils/countryDisplayName'

type LocaleOption = {
  label: string
  value: { id: Bcp47Locale }
}

const props = defineProps<{
  countryCode: CountryCodeValue
  defaultLocale: Locale
  locales: Locale[]
}>()

const i18n = useI18n()
const { getLink } = useLinkResolver()
const { trackCountrySwitchClick } = useTracking()
const experiments = useExperiments()
const route = useRoute()
const enableEsUsLocaleSelector =
  experiments['experiment.enableEsUsLocaleSelector'] !== 'disabled'

const hideLocales = ref<null | (() => void)>(null)

const displayName = countryDisplayNameLocale(
  props.defaultLocale,
  MarketCountryCode[props.countryCode],
)
const currentLocaleInLocaleOptions = props.locales.some(
  (l) => toBcp47Locale(l) === i18n.currentLocale,
)

const selectedLocale = ref<LocaleOption>({
  label: displayName,
  value: { id: toBcp47Locale(props.defaultLocale) },
})

if (currentLocaleInLocaleOptions) {
  selectedLocale.value = {
    label: countryDisplayNameBcp47(
      i18n.currentLocale,
      MarketCountryCode[props.countryCode],
    ),
    value: { id: i18n.currentLocale },
  }
}

const countryName = computed(() => i18n.country(props.countryCode))

const displayMultiLocaleSelector = computed(() => {
  return enableEsUsLocaleSelector && props.locales && props.locales.length > 1
})

const localeOptions = computed(() => {
  if (!props.locales?.length) return []

  return props.locales.map((l) => ({
    label: countryDisplayNameLocale(l, MarketCountryCode[props.countryCode]),
    value: { id: toBcp47Locale(l) },
  }))
})

const link = computed(
  () =>
    getLink({
      link: { name: HOME },
      countryCode: MarketCountryCode[props.countryCode],
    })?.href ?? '#',
)

const trackCountryChange = (countryCode: CountryCodeValue) => {
  trackCountrySwitchClick({
    name: 'subnav_country_switch',
    zone: 'header',
    value: { countryCode },
  })
}

function getLocaleLink(selectedOption: LocaleOption) {
  return (
    getLink({
      link: { name: route.name, query: route.query },
      countryCode: MarketCountryCode[props.countryCode],
      overrideLocale: selectedOption.value.id,
    })?.href ?? '#'
  )
}

function onLocalesClick(
  isOpen: boolean,
  show: () => Promise<void>,
  hide: () => void,
) {
  if (isOpen) hide()
  else void show()

  hideLocales.value = hide
}

function onParentLanguageMenuScroll() {
  if (typeof hideLocales.value !== 'function') return

  hideLocales.value()
}

onMounted(() => {
  const parentLocalesListElement = document.getElementById(
    'country-switch-countries-list',
  )
  if (!parentLocalesListElement) return

  parentLocalesListElement.addEventListener(
    'scroll',
    onParentLanguageMenuScroll,
  )
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onParentLanguageMenuScroll)
})
</script>
